import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import useDedicatedFileUpload from './useDedicatedFileUpload';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import { buildLiabilityPaths } from '../utils/caseFieldPaths';
import { prepFileMetaDataForDedicatedUpload } from '../utils/helper';
import { RECORD_DATA_TYPE, UPDATE_CASE_ACTION } from '@demandproai/shared';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { updateCaseDataFile } from '../redux/actions/fileUpload';
import useCaseRecordDelete from './useCaseRecordDelete';

// Manages accident scene image operations
const useCaseAccidentScene = () => {
  const { caseId } = useParams();
  const dispatch = useDispatch();
  const { formik } = useAddCaseFormContext();
  const { handleFileRemove } = useCaseRecordDelete();
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const caseDataFiles = useSelector(state => state.fileUpload.caseDataFiles);
  const { isLoading: isFileLoading, dedicatedImageUpload, completeFileUpload, startFileUpload } =
    useDedicatedFileUpload(caseId, RECORD_DATA_TYPE.LIABILITY);
  const { updateCase } = useCaseFieldUpdate({ caseId });
  const libilityPaths = buildLiabilityPaths();

  const isLoading = isFileLoading || isUploadingFiles;

  // Upload accident scene images
  const handleAccidentSceneUpload = useCallback(async (data) => {
    const newFiles = data.filter(f => f.file && !f.s3UrlPath);

    formik.setFieldValue('liability.accidentSceneFiles', data);

    // Early return if no new files to upload
    if (newFiles.length === 0) return;

    // Start upload process
    setIsUploadingFiles(true);
    startFileUpload(newFiles.length);

    try {
      // Prepare and upload files
      const prepFiles = prepFileMetaDataForDedicatedUpload(newFiles);
      const uploadedFiles = await dedicatedImageUpload(prepFiles, {
        fieldKey: libilityPaths.accidentSceneFiles.base,
        action: 'add'
      });

      // Merge uploaded data with original file data, keeping original url for preview
      const mergedFiles = uploadedFiles.map((uploaded, index) => ({
        ...newFiles[index],
        ...uploaded,
        url: newFiles[index].url
      }));

      // Combine already uploaded files with newly uploaded files
      const existingFiles = data.filter(f => f.s3UrlPath);
      const allFiles = [...existingFiles, ...mergedFiles];

      formik.setFieldValue('liability.accidentSceneFiles', allFiles);
      completeFileUpload(newFiles.length);
    } catch (error) {
      console.error('Error uploading accident scene images:', error);
      completeFileUpload(newFiles.length);
      // Revert to previous state on error
      formik.setFieldValue('liability.accidentSceneFiles', formik.values.liability?.accidentSceneFiles || []);
    } finally {
      setIsUploadingFiles(false);
    }
  }, [formik, dedicatedImageUpload, startFileUpload, completeFileUpload, libilityPaths]);

  // Remove accident scene image
  const handleAccidentSceneRemove = useCallback(async (s3UrlPath) => {
    handleFileRemove({
        formik,
        fileToRemove:{s3UrlPath},
        removeData: { [libilityPaths.accidentSceneFiles.base]: { s3UrlPath } }
    });
  }, [formik,libilityPaths,handleFileRemove]);

  // Update accident scene image selection
  const handleAccidentSceneIsSelectedChange = useCallback(async ({ s3UrlPath, isSelected }) => {
    const files = formik.values.liability?.accidentSceneFiles || [];
    const fileIndex = files.findIndex(file => file.s3UrlPath === s3UrlPath);

    if (fileIndex !== -1) {
      if(caseDataFiles.get(s3UrlPath)){
        dispatch(updateCaseDataFile({s3UrlPath, isLiability:isSelected}));
      }else{
        await updateCase({
          data: { [libilityPaths.accidentSceneFiles.isLiability(fileIndex)]: isSelected },
          action: UPDATE_CASE_ACTION.UPDATE
        });
      }
    }
  }, [formik.values.liability?.accidentSceneFiles, updateCase, libilityPaths, caseDataFiles, dispatch]);

  // Update defendants associated with accident scene image
  const handleAccidentSceneDefendantsChange = useCallback(async ({ s3UrlPath, defendants }) => {
    const files = formik.values.liability?.accidentSceneFiles || [];
    const fileIndex = files.findIndex(file => file.s3UrlPath === s3UrlPath);

    if (fileIndex !== -1) {
      const caseDataFile = caseDataFiles.get(s3UrlPath);
      if(caseDataFile){
          dispatch(updateCaseDataFile({s3UrlPath, metaData:{...caseDataFile.metaData,defendants}}));
      }else{
        await updateCase({
          data: { [libilityPaths.accidentSceneFiles.defendants(fileIndex)]: defendants },
          action: UPDATE_CASE_ACTION.UPDATE
        });
      }
    }
  }, [formik.values.liability?.accidentSceneFiles, updateCase, libilityPaths, caseDataFiles, dispatch]);

  return {
    isLoading,
    handleAccidentSceneUpload,
    handleAccidentSceneRemove,
    handleAccidentSceneIsSelectedChange,
    handleAccidentSceneDefendantsChange
  };
};

export default useCaseAccidentScene;
